import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { titleCase } from '../../lib/format'
import { useStorageUpload } from '../../lib/upload'
import type { MemberProfile, RoleCatalogEntry } from '../../types'
import {
  Alert, Avatar, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { IdIcon } from '../../components/icons'

interface MemberResponse { ok: boolean; profile: MemberProfile }

export default function MemberDetail() {
  const { uid } = useParams<{ uid: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [editing, setEditing] = useState(false)

  const memberQuery = useQuery({
    queryKey: ['leader', 'team', uid],
    queryFn: () => api.get<MemberResponse>(`/leader/team/${uid}`),
    enabled: Boolean(uid),
  })
  const rolesQuery = useQuery({
    queryKey: ['leader', 'assignable-roles'],
    queryFn: () => api.get<{ ok: boolean; roles: RoleCatalogEntry[] }>('/leader/assignable-roles'),
    enabled: editing,
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<{ ok: boolean; status: string }>(
      `/leader/members/${uid}`, body,
    ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['leader', 'team'] })
      setEditing(false)
      if (res.status === 'pending') {
        window.alert('Changes submitted for approval - a different admin needs to sign off before they take effect.')
      }
    },
  })

  const deactivate = useMutation({
    mutationFn: () => api.post(`/leader/members/${uid}/deactivate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'team'] })
      navigate('/team')
    },
  })

  const photoInputRef = useRef<HTMLInputElement>(null)
  const photoUpload = useStorageUpload(`profile_photos/${user?.org_id ?? ''}/${uid ?? ''}`)
  const savePhoto = useMutation({
    mutationFn: (photo_url: string) => api.post(`/leader/members/${uid}/photo`, { photo_url }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'team', uid] })
      void qc.invalidateQueries({ queryKey: ['leader', 'team'] })
    },
  })

  async function onPhotoChange() {
    const file = photoInputRef.current?.files?.[0]
    if (!file) return
    try {
      const uploaded = await photoUpload.upload(file)
      savePhoto.mutate(uploaded.url)
    } catch {
      // upload.error already holds a message for the UI
    } finally {
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  if (!uid) return <Navigate to="/team" replace />

  if (memberQuery.isError) {
    return (
      <Alert tone="danger">
        {(memberQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/team">Back to team</Link></div>
      </Alert>
    )
  }
  if (memberQuery.isLoading || !memberQuery.data) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
  }

  const profile = memberQuery.data.profile
  const roles = rolesQuery.data?.roles ?? []

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    save.mutate({
      full_name: String(form.get('full_name') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      role: String(form.get('role') ?? profile.role ?? ''),
      national_id: String(form.get('national_id') ?? '').trim(),
      dob: String(form.get('dob') ?? '').trim(),
      gender: String(form.get('gender') ?? '').trim(),
    })
  }

  return (
    <>
      <div className="page-head">
        <div className="row">
          <Avatar photoUrl={profile.photo_url} name={profile.name} email={profile.email} size={48} />
          <div>
            <h1>{profile.name || profile.email}</h1>
            <p className="page-head__sub">{profile.email}</p>
          </div>
        </div>
        <div className="row">
          <StatusBadge status={profile.status} />
          <Link to={`/team/${uid}/badge`}><Button size="sm" variant="secondary" icon={<IdIcon size={15} />}>Print ID badge</Button></Link>
          {!editing && (
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
          )}
        </div>
      </div>

      {editing ? (
        <Card>
          <CardHeader title="Edit member" sub="Some changes may need a second admin's approval before they apply." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {save.isError && (
                  <Alert tone="danger">
                    {save.error instanceof ApiError ? save.error.message : 'Could not save those changes.'}
                  </Alert>
                )}
                <Input label="Full name" name="full_name" defaultValue={profile.name} />
                <Input label="Phone" name="phone" defaultValue={profile.phone} />
                <Select label="Role" name="role" defaultValue={profile.role} disabled={rolesQuery.isLoading}>
                  {!roles.some((r) => r.key === profile.role) && (
                    <option value={profile.role}>{titleCase(profile.role)}</option>
                  )}
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.title ?? titleCase(r.key)}</option>)}
                </Select>
                <div className="grid grid--2">
                  <Input label="National ID" name="national_id" defaultValue={profile.national_id} />
                  <Input label="Date of birth" name="dob" type="date" defaultValue={profile.dob} />
                </div>
                <Select label="Gender" name="gender" defaultValue={profile.gender}>
                  <option value="">Not specified</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
                <div className="row">
                  <Button type="submit" loading={save.isPending} confirm={{ title: "Save these changes?" }}>Save changes</Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid--2">
          <Card>
            <CardHeader title="Profile" />
            <CardBody>
              <div className="stack">
                <Row label="Role" value={titleCase(profile.role) || '—'} />
                <Row label="Phone" value={profile.phone || '—'} />
                <Row label="National ID" value={profile.national_id || '—'} />
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="ID photo" sub="Used for the printable ID badge." />
            <CardBody>
              <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-4)' }}>
                <Avatar photoUrl={profile.photo_url} name={profile.name} email={profile.email} size={72} />
                <div className="stack" style={{ gap: 'var(--sp-2)' }}>
                  {(photoUpload.error || savePhoto.isError) && (
                    <Alert tone="danger">
                      {photoUpload.error
                        || (savePhoto.error instanceof ApiError ? savePhoto.error.message : 'Could not save that photo.')}
                    </Alert>
                  )}
                  <Input
                    type="file" ref={photoInputRef} accept="image/*"
                    onChange={onPhotoChange}
                    hint="A clear, front-facing photo, plain background if possible."
                  />
                  {(photoUpload.progress !== null || savePhoto.isPending) && (
                    <span className="field__hint">
                      {photoUpload.progress !== null ? `Uploading… ${photoUpload.progress}%` : 'Saving…'}
                    </span>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Danger zone" />
            <CardBody>
              <p className="page-head__sub" style={{ marginTop: 0 }}>
                Deactivating suspends this person's account. They can be reactivated by a super-admin.
              </p>
              {deactivate.isError && (
                <div style={{ marginBottom: 'var(--sp-3)' }}>
                  <Alert tone="danger">{(deactivate.error as Error).message}</Alert>
                </div>
              )}
              <Button
                variant="danger"
                loading={deactivate.isPending}
                onClick={async () => {
                  if (await confirm({
                    title: 'Deactivate this member?',
                    message: 'They will lose access immediately.',
                    destructive: true,
                  })) deactivate.mutate()
                }}
              >
                Deactivate member
              </Button>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
